/**
 * Scope Operate incidents to emails from the project's connected inbox, plus
 * alerts the operator registers manually for this project.
 * Lab / harness / seed alerts stay excluded.
 */
import type { AmsIncident, RepositoryRecord } from '../services/api';

function tokensFromText(raw: string): string[] {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3);
}

function repoBasename(url: string): string {
  try {
    const clean = url.replace(/\.git$/i, '').replace(/\/+$/, '');
    const parts = clean.split('/');
    return parts[parts.length - 1] || clean;
  } catch {
    return url;
  }
}

export function buildRepoScopeTokens(
  projectName?: string | null,
  repositories?: RepositoryRecord[] | null,
): string[] {
  const set = new Set<string>();
  for (const t of tokensFromText(projectName || '')) set.add(t);
  for (const repo of repositories ?? []) {
    for (const t of tokensFromText(repoBasename(repo.url || ''))) set.add(t);
    for (const t of tokensFromText(repo.url || '')) {
      if (t.length >= 4) set.add(t);
    }
  }
  for (const drop of ['http', 'https', 'git', 'com', 'org', 'repo', 'main', 'master']) {
    set.delete(drop);
  }
  return [...set];
}

/** Lab / harness / synthetic titles that must never appear in the inbox list. */
export function isLabHarnessTitle(title: string): boolean {
  const t = title.trim().toLowerCase();
  if (!t) return false;
  return (
    /^wave\d/i.test(t) ||
    /^or\s+claude/i.test(t) ||
    /^go\s+engrepo/i.test(t) ||
    /hypothesis-driven debug/i.test(t) ||
    /\bvalidator-bug\b/i.test(t) ||
    /\btest-reproduce\b/i.test(t) ||
    /\bfull-lifecycle\b/i.test(t) ||
    /\bfinal-run\b/i.test(t) ||
    /\blive-run\b/i.test(t) ||
    /\bengineering-run\b/i.test(t) ||
    /\bharness\b/i.test(t) ||
    /\bsynthetic\b/i.test(t) ||
    /\blab[-_\s]?alert\b/i.test(t) ||
    /\(20\d{12,}\)/.test(t) || // (20260713191252)
    /\b20\d{12,}\b/.test(t)
  );
}

export function hasAppliedRemediation(i: AmsIncident): boolean {
  if (i.resolution === 'operational_remediation_resolved') return true;
  if (i.mission?.resolution === 'operational_remediation_resolved') return true;
  return Boolean(i.rca?.some((r) => String(r.type) === 'operational_remediation'));
}

/**
 * True for mail extracted from the project's connected inbox: the legacy
 * mailbox IMAP sync, or the Shared Inbox (routed or operator-imported).
 * The backend has already applied its relevance gate to both, so they are
 * treated the same here.
 */
function isExtractedInboxMail(payload: Record<string, unknown>): boolean {
  const mode = String(payload.ingestion_mode || '').toLowerCase();
  return Boolean(payload.mailbox_id) || mode === 'imap_sync' || mode === 'shared_inbox';
}

/**
 * True for incidents from the connected inbox (legacy mailbox or Shared
 * Inbox), or alerts the operator registered via Alert configuration on this
 * project.
 * Seed / harness / lab email ingest stays excluded.
 */
export function isConnectedInboxEmail(inc: AmsIncident): boolean {
  const src = (inc.source || '').toLowerCase();
  if (!(src === 'email' || src.includes('inbox') || src.includes('mail'))) return false;

  const payload = inc.raw_payload ?? {};
  const mode = String(payload.ingestion_mode || '').toLowerCase();

  // Explicit mailbox linkage from IMAP or Shared Inbox extract
  if (isExtractedInboxMail(payload)) return true;

  // Operator-registered via Alert configuration (POST …/ams/incidents, mode=manual)
  if (mode === 'manual') return true;

  // Seeded / harness / lab ingest is not the user's connected inbox
  if (mode === 'seed' || mode === 'harness' || mode === 'lab') return false;

  return false;
}

function incidentMatchesRepo(inc: AmsIncident, tokens: string[]): boolean {
  if (!tokens.length) return true;
  const app = (inc.application_key || '').toLowerCase();
  if (app && tokens.some((t) => app.includes(t) || t.includes(app))) return true;
  const payload = inc.raw_payload ?? {};
  const mode = String(payload.ingestion_mode || '').toLowerCase();
  // Operator-registered alerts are already scoped to this project API call.
  if (mode === 'manual') return true;
  // Inbox-extracted project-relevant emails already passed backend relevance — trust them
  if (isExtractedInboxMail(payload)) {
    if (payload.project_relevant === true || payload.project_actionable === true) return true;
  }
  const hay = `${inc.title}\n${inc.summary || ''}`.toLowerCase();
  return tokens.some((t) => hay.includes(t));
}

export interface RepoScopeOptions {
  projectName?: string | null;
  repositories?: RepositoryRecord[] | null;
  /** When true, require incidents from the connected mailbox IMAP sync. */
  inboxOnly?: boolean;
  keepRemediated?: boolean;
}

/**
 * Filter to project-relevant incidents from the connected inbox (and
 * operator-registered alerts). Drops lab harness / seed noise.
 */
export function filterIncidentsForIngestedRepo(
  incidents: AmsIncident[],
  opts: RepoScopeOptions = {},
): AmsIncident[] {
  const tokens = buildRepoScopeTokens(opts.projectName, opts.repositories);
  const keepRemediated = opts.keepRemediated !== false;
  const inboxOnly = opts.inboxOnly !== false;

  return incidents.filter((inc) => {
    // Always drop lab / harness titles from Operate inbox surfaces
    if (isLabHarnessTitle(inc.title)) return false;

    if (inboxOnly) {
      if (!isConnectedInboxEmail(inc)) return false;
      const payload = inc.raw_payload ?? {};
      if (payload.project_relevant === false) return false;
      // Repo token match when we have tokens; IMAP-relevant emails still pass via incidentMatchesRepo
      if (!incidentMatchesRepo(inc, tokens)) return false;
      return true;
    }

    if (keepRemediated && hasAppliedRemediation(inc) && isConnectedInboxEmail(inc)) return true;
    if (!isConnectedInboxEmail(inc) && !hasAppliedRemediation(inc)) return false;
    if (!incidentMatchesRepo(inc, tokens)) return false;
    const payload = inc.raw_payload ?? {};
    if (payload.project_relevant === false) return false;
    return true;
  });
}

export function recognitionLabel(inc?: AmsIncident | null): string {
  if (!inc) return '';
  const status = (inc.status || '').toLowerCase();
  if (hasAppliedRemediation(inc)) return 'Fixed';
  if (status === 'analyzing') return 'Recognizing…';
  if (status === 'analyzed' || status === 'resolved' || status === 'closed') return 'Recognized';
  if (inc.rca?.length) return 'Recognized';
  return 'Not recognized';
}

/** @deprecated use isConnectedInboxEmail */
export const isInboxEmail = isConnectedInboxEmail;
